import React, { useEffect } from 'react';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import '../../privacy-policy.css';

const DataDeletion = () => {
  useEffect(() => {
    document.title = "Data Deletion Instructions | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Instructions for users to request deletion of their personal data from PranaFlow Academy databases.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pranaflowacademy.com/data-deletion');

    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-page-container">
      <PublicHeader />

      {/* Hero Header */}
      <section className="privacy-hero">
        <div className="container">
          <span className="privacy-last-updated-badge">Privacy Portal</span>
          <h1 className="privacy-hero-title">Data Deletion Instructions</h1>
          <p className="privacy-hero-subtitle">
            Instructions on how to request the removal of your personal information and academic records from PranaFlow Academy.
          </p>
        </div>
      </section>

      {/* Centered Single-Column Container using standard styles */}
      <main className="privacy-split-wrapper" style={{ gridTemplateColumns: '1fr', maxWidth: '850px', marginTop: '40px' }}>
        <article className="privacy-content-card">
          
          <div className="privacy-intro-section">
            <p>At PranaFlow Academy, we respect your privacy and provide you with control over your personal data.</p>
            <p>
              If you have registered for an account, applied as a teacher, or completed an enquiry and wish to permanently remove your records from our databases, you can submit a deletion request through the methods described below.
            </p>
          </div>

          {/* Section 1 */}
          <section className="privacy-doc-section">
            <h2>How to Submit Your Request</h2>
            <p>You can request data deletion using either of the following official channels:</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px', margin: '24px 0' }}>
              {/* Email Option */}
              <div style={{
                background: '#F3F8F6',
                padding: '24px',
                borderRadius: '12px',
                borderLeft: '4px solid #B9853D',
                border: '1px solid #E3ECE8',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div>
                  <strong style={{ color: '#0D443E', fontSize: '1.05rem', display: 'block', marginBottom: '8px' }}>
                    Option 1: Send an Email
                  </strong>
                  <p style={{ color: '#5E6B68', fontSize: '0.85rem', lineHeight: 1.5, margin: 0 }}>
                    Submit a written request to our privacy team from your registered email address.
                  </p>
                </div>
                <a href="mailto:pranaflowacademy@gmail.com" style={{
                  color: '#B9853D',
                  fontWeight: 700,
                  textDecoration: 'none',
                  marginTop: '16px',
                  fontSize: '0.9rem',
                  display: 'block'
                }}>
                  pranaflowacademy@gmail.com
                </a>
              </div>

              {/* WhatsApp Option */}
              <div style={{
                background: '#F3F8F6',
                padding: '24px',
                borderRadius: '12px',
                borderLeft: '4px solid #0D443E',
                border: '1px solid #E3ECE8',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <div>
                  <strong style={{ color: '#0D443E', fontSize: '1.05rem', display: 'block', marginBottom: '8px' }}>
                    Option 2: WhatsApp Chat
                  </strong>
                  <p style={{ color: '#5E6B68', fontSize: '0.85rem', lineHeight: 1.5, margin: 0 }}>
                    Message our official support channel to initialize verification.
                  </p>
                </div>
                <a href="https://wa.me/919548354084" target="_blank" rel="noopener noreferrer" style={{
                  color: '#0D443E',
                  fontWeight: 700,
                  textDecoration: 'none',
                  marginTop: '16px',
                  fontSize: '0.9rem',
                  display: 'block'
                }}>
                  +91 9548354084
                </a>
              </div>
            </div>
          </section>

          <hr className="privacy-divider" />

          {/* Section 2 */}
          <section className="privacy-doc-section">
            <h2>Required Details for Verification</h2>
            <p>To securely locate and verify your identity before deleting any records, please include the following details in your request:</p>
            <ul>
              <li>Your <strong>Full Name</strong> as registered on our platform</li>
              <li>Your <strong>Registered Email Address</strong></li>
              <li>Your <strong>Phone Number / WhatsApp Number</strong></li>
              <li>The <strong>Course Name</strong> you enrolled in or inquired about (if applicable)</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 3 */}
          <section className="privacy-doc-section">
            <h2>Our Review & Processing Timeline</h2>
            <p>PranaFlow Academy will review and process valid deletion requests according to applicable privacy laws and regulations. Once we confirm and verify your identity:</p>
            <ul>
              <li>Your profile details, course progress, logs, and dashboard access will be deactivated.</li>
              <li>All identifiable information will be permanently deleted or anonymized from active database tables.</li>
              <li>You will receive a confirmation email or message once the deletion is finalized (usually processed within 15 days).</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 4 */}
          <section className="privacy-doc-section">
            <div style={{
              background: '#FFF8F6',
              border: '1px solid #FFE4E0',
              color: '#B83214',
              padding: '24px',
              borderRadius: '12px',
              fontSize: '0.92rem',
              lineHeight: 1.6
            }}>
              <strong>⚠️ Important Notice Regarding Data Retention:</strong> Please note that some financial, payment, tax, certificate validation, or regulatory audit records may be retained in secure archives for compliance reasons. These archived records will only be used to meet legal, accounting, and certificate verification obligations, and will not be accessible for active marketing or academy dashboard operations.
            </div>
          </section>

          {/* Bottom CTA section */}
          <div className="privacy-cta-section">
            <h4 className="privacy-cta-title">Have questions about your data?</h4>
            <p className="privacy-cta-desc">Feel free to connect with our support team via email or WhatsApp anytime.</p>
            
            <div className="privacy-cta-buttons">
              <a href="mailto:pranaflowacademy@gmail.com" className="privacy-cta-btn email">
                <svg viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <span>pranaflowacademy@gmail.com</span>
              </a>
              <a href="https://wa.me/919548354084" target="_blank" rel="noopener noreferrer" className="privacy-cta-btn whatsapp">
                <svg viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.731-1.456L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.859-4.407 9.862-9.823.002-2.623-1.017-5.09-2.871-6.944-1.853-1.854-4.321-2.87-6.944-2.872-5.442 0-9.863 4.41-9.865 9.827-.001 1.771.468 3.498 1.36 5.03l-.993 3.624 3.731-.977c-.105-.045-.2-.09-.285-.118zm10.741-7.234c-.29-.145-1.716-.848-1.982-.944-.266-.096-.46-.145-.653.145-.194.291-.75.944-.919 1.138-.169.194-.339.218-.629.073-.29-.145-1.226-.452-2.335-1.441-.864-.771-1.447-1.724-1.616-2.015-.17-.29-.018-.447.127-.591.131-.13.29-.339.436-.509.145-.17.194-.291.29-.485.097-.194.049-.364-.025-.509-.073-.145-.653-1.575-.895-2.156-.236-.569-.475-.491-.653-.5H6.99c-.194 0-.509.073-.775.364-.266.291-1.017.994-1.017 2.424s1.041 2.812 1.186 3.006c.145.194 2.049 3.129 4.965 4.385.694.299 1.236.478 1.658.613.697.221 1.332.19 1.833.115.559-.084 1.716-.703 1.958-1.382.242-.679.242-1.261.169-1.382-.072-.12-.266-.193-.556-.339z"/>
            </svg>
            <span>WhatsApp Chat</span>
          </a>
        </div>
      </div>

        </article>
      </main>

      <PublicFooter />
    </div>
  );
};

export default DataDeletion;
